import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageCircle, Sparkles, Target, Brain, ArrowRight, Smartphone, Apple } from 'lucide-react';

const CLAYO_IMG = '/assets/img/clayo-mascot.jpg';

const capabilities = [
  { icon: Brain, label: "Analyse tes données" },
  { icon: Target, label: "Conseille sur ton fusil & tes chokes" },
  { icon: Sparkles, label: "Plan de progression personnalisé" },
];

export default function ClayoSection() {
  return (
    <section id="clayo" data-testid="clayo-section" className="relative py-24 sm:py-32 overflow-hidden bg-[#0D0D0D]">
      {/* Background glow (light, behind text) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FF6B35]/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Mascot */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7 }}
            className="relative flex justify-center lg:justify-start"
          >
            <div className="relative">
              <img
                src={CLAYO_IMG}
                alt="Clayo, Coach IA ClayPulse"
                data-testid="clayo-mascot-img"
                className="relative w-[280px] sm:w-[360px] lg:w-[440px] h-auto object-contain mix-blend-lighten"
                loading="lazy"
              />
              {/* Online status badge */}
              <div className="absolute top-4 right-4 sm:top-8 sm:right-8 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/10">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                </span>
                <span className="text-white text-xs font-medium">En ligne</span>
              </div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 mb-6">
              <Sparkles size={14} className="text-[#FF6B35]" />
              <span className="text-[#FF6B35] text-xs font-semibold uppercase tracking-wider">Nouveau — Coach IA intégré</span>
            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Poppins'] leading-tight mb-6">
              Rencontre <span className="text-[#FF6B35]">Clayo</span>,<br />
              ton coach IA ball-trap
            </h2>

            <p className="text-[#A0A0A0] text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
              Clayo analyse tes sessions, tes points faibles et ton matériel pour te donner des conseils
              précis et personnalisés — comme un vrai coach, dispo 24/7 dans ta poche.
            </p>

            <div className="space-y-3 mb-8">
              {capabilities.map((cap, i) => (
                <motion.div
                  key={cap.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#FF6B35]/10 border border-[#FF6B35]/20 flex items-center justify-center shrink-0">
                    <cap.icon size={16} className="text-[#FF6B35]" />
                  </div>
                  <span className="text-white/90 text-sm sm:text-base">{cap.label}</span>
                </motion.div>
              ))}
            </div>

            {/* Availability badges */}
            <div className="flex flex-wrap gap-3 mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30">
                <Smartphone size={14} className="text-green-400" />
                <span className="text-green-400 text-xs sm:text-sm font-medium">Disponible sur Android</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30">
                <Apple size={14} className="text-green-400" />
                <span className="text-green-400 text-xs sm:text-sm font-medium">Disponible sur l&apos;App Store</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/clayo"
                data-testid="clayo-cta-discover"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white font-semibold hover:shadow-[0_0_30px_rgba(255,107,53,0.4)] transition-all duration-300 hover:scale-105"
              >
                <MessageCircle size={18} />
                Découvrir Clayo
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
